import { CloseOutlined, DownOutlined, PlusCircleOutlined, SwapRightOutlined } from '@ant-design/icons';
import { useControllableValue } from 'ahooks';
import {
  AutoComplete,
  Button,
  Dropdown,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Select,
  SelectProps,
  Space,
  Tooltip,
} from 'antd';
import classNames from 'classnames';
import { get, isArray, isEmpty, remove } from 'lodash-es';

import React, { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DATA_TID } from '@/infrastructure/cypress-ids';
import { getProductId } from '@/infrastructure/product-info';
import { FormEntry } from '@/presentation/components/form';
import { getBuglyCustomKeys, getCustomUserKeys, getExtendInfoKeys } from '@/presentation/components/form/api';
import { FieldsOperator, fieldsOperators, FieldValueType } from '@/presentation/components/form/proto';
import { MATCH_MODE_TEXT, MatchMode } from '@/presentation/components/form/standards/types';
import { TdsFieldSelector, TdsFieldSelectorProps } from '@/presentation/components/tds-form/tds-field-selector';
import { IssueType } from '@/proto/issue';
import { KeyDataType } from '@/proto/token';
import { IUserKey } from '@/proto/user';
import { toLineString } from '@raft/utils/src/case/snake-case';
import { BaseFormItemProps } from '../type';
import styles from './index.module.scss';

export interface IProps {
  defaultValue?: any;
  value?: any;
  entry: FormEntry;
  issueType?: IssueType;
  type: CustomType;
}

export interface TdsCustomFieldsItemProps {
  index: number;
  columns?: IUserKey[];
  onDelete?: (index: number) => void;
  form: FormInstance;
  onChange?: (values: CustomFieldValue, index: number) => void;
  type: CustomType;
  keyComponentType?: KeyComponentType;
  loading?: boolean;
  name: string;
  customOperatorOptions?: SelectProps['options'];
  valueSelectProps?: TdsFieldSelectorProps;
  keyTransform?: (key: string) => string;
}

export interface TdsCustomFieldsPanelProps extends BaseFormItemProps<any> {
  // name: string;
  issueType: IssueType;
  type: CustomType;
  keyComponentType?: KeyComponentType;
  className?: string
  style?: CSSProperties;
  initCustomKeys?: IUserKey[];
  customOperatorOptions?: SelectProps['options'];
  valueSelectProps?: TdsFieldSelectorProps;
  keyOptionsTransform?: (keys: IUserKey[]) => IUserKey[];
  keyTransform?: (key: string) => string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  customQueryKeys?: () => Promise<IUserKey[]>;
}

export type CustomType = 'customKey' | 'customFields' | 'buglyCustomKey';

export type KeyComponentType = 'autoComplete' | 'select';

export type CustomKey = {
  label: string;
  key: string;
  type: number;
};

export type CustomFieldValue = {
  operator?: string;
  key?: string;
  values?: string[] | string;
  type?: KeyDataType;
  name?: string;
};

// cypress test id
const { addBtn, confirmBtn, cancelBtn, itemDelete, itemKey, itemOP, itemValue } = DATA_TID.customField;

export const defaultFilter = {
  name: undefined,
  operator: undefined,
  values: undefined,
};

type RangeValue = [number | undefined, number | undefined];

const InputNumberRange = (props: {
  onChange?: (rangeValue: RangeValue) => void;
  value?: RangeValue;
  defaultValue?: RangeValue;
  className?: string;
}) => {
  const { defaultValue = [undefined, undefined] } = props;
  const [rangeValue, setRangeValue] = useState<RangeValue>(props.value || defaultValue);

  const onChage = (value: number, index: number) => {
    const range = [...rangeValue];
    range[index] = value;
    setRangeValue(range as RangeValue);
    props.onChange?.(range as RangeValue);
  };

  useEffect(() => {
    setRangeValue(rangeValue);
  }, [props.value]);

  return (
    <div className={classNames(props.className, styles.inputRange)}>
      <InputNumber
        value={rangeValue[0]}
        onChange={value => onChage(value as number, 0)}
        data-tid={itemValue.numberFrom}
      />
      <SwapRightOutlined />
      <InputNumber
        value={rangeValue[1]}
        onChange={value => onChage(value as number, 1)}
        data-tid={itemValue.numberTo}
      />
    </div>
  );
};

export const CustomFieldsItem: React.FC<TdsCustomFieldsItemProps> = (props) => {
  const { t } = useTranslation();
  const {
    columns = [], form, index, type, keyComponentType,
    customOperatorOptions, valueSelectProps, keyTransform,
  } = props;
  const [selectedKey, setSelectedKey] = useState<CustomKey | null>(null);
  const [keyValue, setKeyValue] = useState<string | undefined>(undefined);
  const [operator, setOperator] = useState<FieldsOperator | null>(null);

  const KEY_COMPONENT_MAP: Record<string, typeof AutoComplete | typeof Select> = {
    select: Select,
    autoComplete: AutoComplete,
  };
  const defaultKeyComponent = type === 'buglyCustomKey' ? AutoComplete : Select;
  const KeyComponent = (keyComponentType && KEY_COMPONENT_MAP[keyComponentType])
    || defaultKeyComponent;

  const onDelete = () => {
    props.onDelete?.(props.index);
  };

  const customPrefix = `${props.name}`;

  const operatorOptions: any = useMemo(
    () => {
      if (customOperatorOptions) {
        return customOperatorOptions.map(option => ({
          ...option,
          label: typeof option.label === 'string' ? t(option.label) : option.label,
        }));
      }
      if (type === 'buglyCustomKey') {
        return [
          {
            label: t('是'),
            value: FieldsOperator.OR,
            ['data-tid']: `${customPrefix}-operator-是-${index}`,
          },
          {
            label: t('不是'),
            value: FieldsOperator.NOT,
            ['data-tid']: `${customPrefix}-operator-不是-${index}`,
          },
          {
            label: t('大于'),
            value: FieldsOperator.Gt,
            ['data-tid']: `${customPrefix}-operator-大于-${index}`,
          },
          {
            label: t('大于等于'),
            value: FieldsOperator.Ge,
            ['data-tid']: `${customPrefix}-operator-大于等于-${index}`,
          },
          {
            label: t('小于'),
            value: FieldsOperator.Lt,
            ['data-tid']: `${customPrefix}-operator-小于-${index}`,
          },
          {
            label: t('小于等于'),
            value: FieldsOperator.Le,
            ['data-tid']: `${customPrefix}-operator-小于等于-${index}`,
          },
          {
            label: t('匹配'),
            value: FieldsOperator.MATCH,
            ['data-tid']: `${customPrefix}-operator-匹配-${index}`,
          },
          {
            label: t('不匹配'),
            value: FieldsOperator.NOTMATCH,
            ['data-tid']: `${customPrefix}-operator-不匹配-${index}`,
          },
        ];
      }
      const options = fieldsOperators[selectedKey?.type as FieldValueType] ?? [];

      return options.map(item => ({
        ...item,
        label: t(item.label),
        ['data-tid']: `${customPrefix}-operator-${item.label}-${index}`,
      }));
    },
    [customOperatorOptions, customPrefix, index, selectedKey, t, type],
  );

  const customKeysOptions = useMemo(() => columns.map(item => ({
    label: item.label ? t(item.label) : '',
    value: item.key,
    ['data-tid']: `${customPrefix}-name-${item.label}-${index}`,
  })), [columns, customPrefix, index, t]);

  const onKeyChange = (key: string) => {
    const currentKey = columns?.find(item => item.key === key);
    setSelectedKey(currentKey as CustomKey);
    const effectiveKey = currentKey?.key || key;
    setKeyValue(effectiveKey || undefined);
    form.setFieldsValue({
      key: effectiveKey,
      type: currentKey?.type || KeyDataType.STRING,
    });
    if (currentKey?.type !== undefined && type !== 'buglyCustomKey') {
      form.setFieldsValue({
        operator: get(fieldsOperators, currentKey?.type as KeyDataType)?.[0]?.value,
      });
    }
  };

  const onOperatorChange = (operator: string) => {
    const op = operator === '' ? FieldsOperator.Eq : (operator as FieldsOperator);
    setOperator(op);
    form.setFieldsValue({
      values: undefined,
      operator: op,
    });
  };

  const renderFieldValueItem = (type: KeyDataType) => {
    if (type === KeyDataType.NUMBER) {
      return operator === FieldsOperator.Between ? (
        <InputNumberRange className={styles.rangeValues} />
      )
        : (
          <InputNumber
            className={styles.values}
            placeholder={t('值')}
            data-tid={`${itemValue.number}-${index}`}
          />
        );
    }

    if (valueSelectProps && (keyValue || valueSelectProps.fieldKey)) {
      const transformedFieldKey = keyTransform && keyValue ? keyTransform(keyValue) : keyValue;
      return <TdsFieldSelector
        key={keyValue}
        {...valueSelectProps}
        fieldKey={transformedFieldKey || valueSelectProps.fieldKey}
        forceRemote
        mode="tags"
        className={styles.values}
        placeholder={t('值')}
        maxTagCount='responsive'
        showSearch
        disabled={[MatchMode.ISEMPTY, MatchMode.NOTEMPTY].includes(operator as any)}
        data-tid={`${itemValue.default}-${index}`}
      />;
    }

    if (type === KeyDataType.ARRAY) {
      return <Select
        className={styles.values}
        mode="tags"
        open={false}
        placeholder={t('值')}
        maxTagCount='responsive'
        showSearch
        data-tid={`${itemValue.array}-${index}`}
      />;
    }

    if (type === KeyDataType.STRING) {
      return <Input
        list="none"
        autoComplete="off"
        className={styles.values}
        placeholder={t('值')}
        data-tid={`${itemValue.array}-${index}`}
      />;
    }

    return <Select
      mode={'tags'}
      className={styles.values}
      placeholder={'Value'}
      data-tid={`${itemValue.default}-${index}`}
      disabled={[MatchMode.ISEMPTY, MatchMode.NOTEMPTY].includes(operator as any)}
    />;
  };

  useEffect(() => {
    const value = form.getFieldsValue();
    value && onKeyChange(value.key);
    value.operator !== undefined && onOperatorChange(value.operator);
    // 重新设置一次值，让更新的选项可以选上
    form.setFieldsValue({
      ...value,
      operator: value.operator === '' ? FieldsOperator.Eq : (value.operator as FieldsOperator),
    });
  }, []);

  return (
    <Form
      preserve
      form={form}
      className={styles.customForm}
      onFieldsChange={() => {
        props.onChange?.(form.getFieldsValue(), index);
      }}>
      <Form.Item hidden name='type' />
      <Space size={8}>
        <Form.Item
          name='key'
        >
          <KeyComponent
            filterOption={(inputValue, option) => option!.label?.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
            }
            className={styles.column}
            options={customKeysOptions}
            placeholder={t('Key或别名')}
            onChange={onKeyChange}
            showSearch
            dropdownMatchSelectWidth={250}
            data-tid={`${itemKey}-${index}`}
          />
        </Form.Item>
        <Form.Item
          name='operator'
          initialValue={operatorOptions?.[0]?.value}
        >
          <Select
            options={operatorOptions}
            className={styles.operator}
            placeholder={t('请选择')}
            onChange={onOperatorChange}
            data-tid={`${itemOP}-${index}`}
          />
        </Form.Item>
        <Form.Item
          name='values'
        >
          {renderFieldValueItem(selectedKey?.type as KeyDataType)}
        </Form.Item>
        <CloseOutlined data-tid={`${itemDelete}-${index}`} className={styles.close} onClick={onDelete} />
      </Space>
    </Form>
  );
};

/**
 * 编辑器核心：管理条件行的状态、Key 加载、确认/取消，
 * 不关心外层展示形式（可被 Dropdown overlay 或独立面板复用）。
 */
export interface CustomFieldsEditorProps {
  value?: CustomFieldValue[];
  onConfirm?: (value: CustomFieldValue[]) => void;
  onCancel?: () => void;
  issueType?: IssueType;
  type: CustomType;
  keyComponentType?: KeyComponentType;
  customOperatorOptions?: SelectProps['options'];
  valueSelectProps?: TdsFieldSelectorProps;
  keyOptionsTransform?: (keys: IUserKey[]) => IUserKey[];
  keyTransform?: (key: string) => string;
  initCustomKeys?: IUserKey[];
  className?: string;
  customQueryKeys?: () => Promise<IUserKey[]>;
}

export const CustomFieldsEditor: React.FC<CustomFieldsEditorProps> = (props) => {
  const { t } = useTranslation();
  const {
    value,
    onConfirm,
    onCancel,
    issueType,
    type,
    keyComponentType,
    customOperatorOptions,
    valueSelectProps,
    keyTransform,
    initCustomKeys,
    keyOptionsTransform,
    className,
    customQueryKeys,
  } = props;

  const [customKeys, setCustomKeys] = useState<IUserKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [customFilters, setCustomFilters] = useState<CustomFieldValue[]>(isEmpty(value) ? [defaultFilter] : value!);
  const forms = useRef<FormInstance[]>([]);

  useEffect(() => {
    const load = async () => {
      const service = type === 'customKey' ? getExtendInfoKeys : getCustomUserKeys;
      setLoading(true);
      try {
        if (customQueryKeys) {
          const keys = await customQueryKeys();
          setCustomKeys(keyOptionsTransform ? keyOptionsTransform(keys) : keys);
        } else if (type === 'buglyCustomKey' && !customQueryKeys) {
          const keys = await getBuglyCustomKeys();
          const rawKeys = keys.map((key: string | { key?: string | null; type?: KeyDataType | null }) => (typeof key === 'string' ? {
            label: key,
            key,
            type: KeyDataType.STRING,
          } : {
            label: key.key || '',
            key: key.key || '',
            type: key.type ?? KeyDataType.STRING,
          }));
          setCustomKeys(keyOptionsTransform ? keyOptionsTransform(rawKeys) : rawKeys);
        } else if (!customQueryKeys) {
          const keys = await service({
            productId: getProductId(),
            issueType,
          });
          setCustomKeys(keyOptionsTransform ? keyOptionsTransform(keys) : keys);
        }
      } finally {
        setLoading(false);
        setLoaded(true);
      }
    };

    if (!loaded && !initCustomKeys) {
      load();
    } else if (initCustomKeys) {
      setCustomKeys(initCustomKeys);
    }
  }, [issueType]);

  useEffect(() => {
    setCustomFilters(!isEmpty(value) ? value! : [defaultFilter]);
    forms.current = [];
  }, [value]);

  const FieldItemContainer = (containerProps: {
    index: number;
    onDelete?: (index: number) => void;
  }) => {
    const { index, onDelete: onDeleteItem } = containerProps;
    const [form] = Form.useForm();

    if (!forms.current[index]) {
      forms.current[index] = form;
      forms.current[index].setFieldsValue(customFilters[index]);
    }

    return (
      <CustomFieldsItem
        customOperatorOptions={customOperatorOptions}
        form={forms.current[index]}
        index={index}
        columns={customKeys}
        onDelete={onDeleteItem}
        type={type}
        keyComponentType={keyComponentType}
        loading={loading}
        name="custom"
        valueSelectProps={valueSelectProps}
        keyTransform={keyTransform}
      />
    );
  };

  const onDelete = (index: number) => {
    const tempData = [...customFilters];
    const tempForms = [...forms.current];
    remove(tempData, (_: CustomFieldValue, i: number) => index === i);
    remove(tempForms, (_: FormInstance, i: number) => index === i);
    forms.current = tempForms;
    setCustomFilters(tempData);
  };

  const addFilter = () => {
    setCustomFilters([...customFilters, defaultFilter]);
  };

  const getFilterValue = (item: CustomFieldValue) => {
    if ([MatchMode.ISEMPTY, MatchMode.NOTEMPTY].includes(item?.operator as any)) {
      return [];
    }
    const stringValue = `${item?.values as string}`;
    if (!isArray(item.values)) return stringValue?.split(';');
    if (item?.operator === FieldsOperator.Between) return stringValue?.split(';');
    return item?.values;
  };

  const filterEmptyValues = (fieldValues: CustomFieldValue[]) => {
    const values: CustomFieldValue[] = [];
    fieldValues.forEach((item) => {
      const isValidFilterItem = (item.key && item.operator && item.values)
        || [MatchMode.ISEMPTY, MatchMode.NOTEMPTY].includes(item.operator as any);

      if (isValidFilterItem) {
        values.push({
          ...item,
          values: getFilterValue(item),
          name: toLineString('custom'),
          operator: item.operator === FieldsOperator.Eq ? '' : item.operator,
        });
      }
    });
    return values;
  };

  const handleConfirm = () => {
    try {
      const formItemValues = forms.current.map(f => f.getFieldsValue());
      const values = filterEmptyValues(formItemValues);
      onConfirm?.(values);
    } catch {
      // ignore
    }
  };

  const handleCancel = () => {
    setCustomFilters(!isEmpty(value) ? value! : [defaultFilter]);
    onCancel?.();
  };

  return (
    <div className={className ?? styles.customOverlay}>
      <span>{t('以下的关系为全且')}</span>
      <div className={styles.itemContainer}>
        {customFilters.map((_filter, index) => (
          <FieldItemContainer key={index} index={index} onDelete={onDelete} />
        ))}
      </div>
      <div>
        <Space className={styles.addButton} size={3}>
          <PlusCircleOutlined />
          <span data-tid={addBtn} onClick={addFilter}>{t('添加筛选')}</span>
        </Space>
      </div>
      <div className={styles.footer}>
        <Space size={10}>
          <Button data-tid={cancelBtn} onClick={handleCancel}>{t('取消')}</Button>
          <Button data-tid={confirmBtn} type="primary" onClick={handleConfirm}>{t('确定')}</Button>
        </Space>
      </div>
    </div>
  );
};

export const TdsCustomFieldsPanel: React.FC<TdsCustomFieldsPanelProps> = (props) => {
  const { t } = useTranslation();
  const {
    issueType, type, keyComponentType, className, initCustomKeys,
    customOperatorOptions, valueSelectProps, keyOptionsTransform,
    keyTransform, prefix, suffix, customQueryKeys,
  } = props;
  const [value, setValue] = useControllableValue<CustomFieldValue[]>(props, {
    defaultValue: [],
  });
  const [visible, setVisible] = useState(false);
  const [customKeys, setCustomKeys] = useState<IUserKey[]>([]);

  const showOverlay = () => setVisible(true);
  const hideOverlay = () => setVisible(false);

  const getFilterText = (values: CustomFieldValue[]) => values.map((item) => {
    const operatorText = MATCH_MODE_TEXT[(item.operator === '' ? MatchMode.Eq : item.operator) as string];
    const currentKey = customKeys.find(customKey => customKey.key === item.key);
    const currentKeyLabel = currentKey?.label ? t(currentKey.label) : item?.key;
    const vals = [MatchMode.ISEMPTY, MatchMode.NOTEMPTY].includes(item.operator as any) ? '' : JSON.stringify(item.values);
    return `${currentKeyLabel} ${t('的值')} ${t(operatorText)} ${vals}`;
  }).join(t(' 且 '));

  const filterText = useMemo(() => {
    if (!value || isEmpty(value)) return '';
    return getFilterText(value as CustomFieldValue[]);
  }, [value, customKeys, t]);

  // 加载 customKeys 仅用于 filterText 显示（Editor 内部独立管理自己的 customKeys）
  useEffect(() => {
    const load = async () => {
      const service = type === 'customKey' ? getExtendInfoKeys : getCustomUserKeys;
      if (customQueryKeys) {
        const keys = await customQueryKeys();
        setCustomKeys(keyOptionsTransform ? keyOptionsTransform(keys) : keys);
      } else if (type === 'buglyCustomKey' && !customQueryKeys) {
        const keys = await getBuglyCustomKeys() as Array<string | { key?: string | null; type?: KeyDataType | null }>;
        const rawKeys = keys.map(key => (typeof key === 'string' ? {
          label: key,
          key,
          type: KeyDataType.STRING,
        } : {
          label: key.key || '',
          key: key.key || '',
          type: key.type ?? KeyDataType.STRING,
        }));
        setCustomKeys(keyOptionsTransform ? keyOptionsTransform(rawKeys) : rawKeys);
      } else if (!customQueryKeys) {
        const keys = await service({ productId: getProductId(), issueType });
        setCustomKeys(keyOptionsTransform ? keyOptionsTransform(keys) : keys);
      }
    };

    if (visible && customKeys.length === 0 && !initCustomKeys) {
      load();
    } else if (initCustomKeys) {
      setCustomKeys(initCustomKeys);
    }
  }, [issueType, visible]);

  const overlay = (
    <CustomFieldsEditor
      customQueryKeys={customQueryKeys}
      value={value}
      onConfirm={(values) => {
        hideOverlay();
        setValue(values);
      }}
      onCancel={hideOverlay}
      issueType={issueType}
      type={type}
      keyComponentType={keyComponentType}
      customOperatorOptions={customOperatorOptions}
      valueSelectProps={valueSelectProps}
      keyOptionsTransform={keyOptionsTransform}
      keyTransform={keyTransform}
      initCustomKeys={initCustomKeys}
    />
  );

  return (
    <div className={className ? className : styles.panel} style={props.style}>
      <Space size={0} className={styles.panelSpace} direction={'vertical'}>
        <Tooltip title={filterText}>
          <Input
            placeholder={t('多个值通过按回车分隔')}
            onFocus={showOverlay}
            prefix={prefix}
            suffix={suffix ?? <DownOutlined style={{ color: 'rgba(0, 0, 0, 0.25)', fontSize: '12px' }} />}
            style={{ width: '100%' }}
            value={filterText}
          />
        </Tooltip>
        <Dropdown
          overlay={overlay}
          trigger={['click']}
          visible={visible}
          destroyPopupOnHide={false}
          onVisibleChange={v => setVisible(v)}
        >
          <div style={{ height: 1 }} />
        </Dropdown>
      </Space>
    </div>
  );
};
